import React from "react";
import { useLocation } from "react-router-dom";
import ServerError from "@/pages/ServerError";

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class InternalErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  public state: ErrorBoundaryState = {
    hasError: false,
    error: null,
  };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("Uncaught error in Toc Toc application:", error, errorInfo);
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  override render() {
    if (this.state.hasError) {
      return <ServerError error={this.state.error} onRetry={this.handleReset} />;
    }

    return this.props.children;
  }
}

const RouteAwareErrorBoundary: React.FC<ErrorBoundaryProps> = ({ children }) => {
  const location = useLocation();
  const [boundaryKey, setBoundaryKey] = React.useState(0);

  React.useEffect(() => {
    setBoundaryKey((key) => key + 1);
  }, [location.pathname, location.search, location.hash]);

  return <InternalErrorBoundary key={boundaryKey}>{children}</InternalErrorBoundary>;
};

export default RouteAwareErrorBoundary;
